import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const HeroCarousel = ({ slides, loading, error }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-play functionality
  useEffect(() => {
    if (slides && slides.length > 1 && !isPaused) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 3000); // 5 second interval

      return () => clearInterval(interval);
    }
  }, [slides, isPaused]);

  // Handle dot click
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Handle mouse enter/leave for pause on hover
  const handleMouseEnter = () => {
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  // Loading state
  if (loading) {
    return (
      <section className="relative h-[500px]  flex items-center justify-center overflow-hidden bg-gray-200">
        <div className="text-gray-500">Loading hero carousel...</div>
      </section>
    );
  }

  // Error state or no slides
  if (error || !slides || slides.length === 0) {
    return (
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#07a977]/90 to-forest-green/70 z-10"></div>
        <div className="relative z-20 container mx-auto px-4 text-center text-white">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold font-heading mb-4 md:mb-6 leading-tight">
            We Rescue. We Heal. We Protect.
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 max-w-3xl mx-auto text-sand-beige px-2">
            Saving street animals through rescue, medical care, sterilisation, and community awareness.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center px-4">
            
            <Link to="/emergency">
              <Button size="lg" variant="outline" className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-forest-green text-base md:text-lg px-6 md:px-8 py-4 md:py-6 transition-all duration-300 hover:scale-105">
                Report an Injured Animal
              </Button>
            </Link>
            <Link to="/adopt">
              <Button size="lg" variant="outline" className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-forest-green text-base md:text-lg px-6 md:px-8 py-4 md:py-6 transition-all duration-300 hover:scale-105">
                Adopt a Furry Friend
              </Button>
            </Link>
          </div>
        </div>
      </section>
    );
  }

  const currentSlideData = slides[currentSlide];

  return (
    <section
      className="relative h-[500px]  flex items-center justify-center overflow-hidden"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-label="Hero carousel"
    >
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-forest-green/90 to-forest-green/70 z-10"></div>

      {/* Slide images with fade transition */}
      <div className="absolute inset-0 w-full h-full">
        {slides.map((slide, index) => (
          <img
            key={slide.id}
            src={slide.image}
            alt={slide.title}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            aria-hidden={index !== currentSlide}
          />
        ))}
      </div>

      {/* Content overlay */}
      <div className="relative z-20 container mx-auto px-4 text-center text-white w-full">
        <div
          className="transition-opacity duration-1000"
          key={currentSlide}
        >
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold font-heading mb-4 md:mb-6 leading-tight">
            {currentSlideData.title}
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 max-w-3xl mx-auto text-sand-beige px-2">
            {currentSlideData.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center px-4">
            
            <Link to="/emergency">
              <Button size="lg" variant="outline" className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-forest-green text-base md:text-lg px-6 md:px-8 py-4 md:py-6 transition-all duration-300 hover:scale-105">
                Report an Injured Animal
              </Button>
            </Link>
            <Link to="/adopt">
              <Button size="lg" variant="outline" className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-forest-green text-base md:text-lg px-6 md:px-8 py-4 md:py-6 transition-all duration-300 hover:scale-105">
                Adopt a Furry Friend
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Dot indicators */}
      {slides.length > 1 && (
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-30 flex gap-2">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentSlide
                ? 'bg-warm-amber w-8'
                : 'bg-white/50 hover:bg-white/75'
                }`}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={index === currentSlide ? 'true' : 'false'}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default HeroCarousel;

